import React, { useCallback } from "react";

import { useHistory } from "react-router";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import ConstructionIcon from "@mui/icons-material/Construction";
import NearMeIcon from "@mui/icons-material/NearMe";
import { alpha, Box, Button, Card, Stack, Typography } from "@mui/material";

import { useCustomerContext } from "../../../Context/CustomerContext";

type Step = {
  title: string;
  description: string;
  icon: React.JSX.Element;
};

const steps: Step[] = [
  {
    title: "Create",
    description: "Start your CloudFlow journey with one of our existing blueprints or start from scratch",
    icon: <BorderColorIcon color="primary" />,
  },
  {
    title: "Build",
    description: "Configure your CloudFlow to complete the task that you want it to using actions and operators",
    icon: <ConstructionIcon color="primary" />,
  },
  {
    title: "Publish",
    description: "Once permissions and validations are in place, you're ready to publish and automate",
    icon: <NearMeIcon color="primary" />,
  },
];

type StepCardProps = {
  step: Step;
  index: number;
  isLastStep: boolean;
};

const StepCard = ({ step, index, isLastStep }: StepCardProps) => (
  <Stack direction="row" alignItems="center" spacing={2}>
    <Card
      sx={(theme) => ({
        p: 3,
        borderRadius: 2,
        borderTop: "4px solid",
        borderTopColor: alpha(theme.palette.primary.main, 0.15),
      })}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              p: 0.75,
              borderRadius: 1,
              backgroundColor: "general.backgroundDark",
              width: 36,
              height: 36,
            }}
          >
            {step.icon}
          </Box>
          <Stack direction="column">
            <Typography variant="caption" color="text.secondary">
              Step {index + 1}
            </Typography>
            <Typography variant="subtitle1" color="text.primary" fontWeight={500}>
              {step.title}
            </Typography>
          </Stack>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {step.description}
        </Typography>
      </Stack>
    </Card>
    {!isLastStep && (
      <ArrowForwardIcon
        sx={{
          color: "general.divider",
          display: { xs: "none", sm: "block" },
        }}
      />
    )}
  </Stack>
);

type ActionButtonsProps = {
  onGetStarted: () => void;
  onViewBlueprints: () => void;
  isLoading: boolean;
};

const ActionButtons = ({ onGetStarted, onViewBlueprints, isLoading }: ActionButtonsProps) => (
  <Stack
    direction="row"
    spacing={2}
    sx={{
      mt: 4,
      justifyContent: "center",
    }}
  >
    <Button
      variant="contained"
      color="primary"
      onClick={onGetStarted}
      loading={isLoading}
      data-cy="publish-cloudflow-get-started-button"
    >
      Get started
    </Button>
    <Button
      variant="text"
      color="primary"
      onClick={onViewBlueprints}
      data-cy="publish-cloudflow-view-blueprints-button"
    >
      View blueprints
    </Button>
  </Stack>
);

export const PublishCloudflowPrompt = ({
  onGetStarted,
  createCloudFlowLoading,
}: {
  onGetStarted: () => void;
  createCloudFlowLoading: boolean;
}) => {
  const history = useHistory();
  const { customer } = useCustomerContext();

  const onClickViewBlueprints = useCallback(() => {
    history.push(`/customers/${customer.id}/cloudflow/blueprints/`);
  }, [history, customer.id]);

  return (
    <Box
      sx={{
        backgroundColor: "general.backgroundDark",
        py: 4,
        px: 2,
        mt: 4,
      }}
    >
      <Typography
        variant="h2"
        sx={{
          mb: 3,
          textAlign: "center",
        }}
      >
        Publish your first CloudFlow
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" justifyContent="space-between">
        {steps.map((step, index) => (
          <StepCard key={step.title} step={step} index={index} isLastStep={index === steps.length - 1} />
        ))}
      </Stack>

      <ActionButtons
        onGetStarted={onGetStarted}
        onViewBlueprints={onClickViewBlueprints}
        isLoading={createCloudFlowLoading}
      />
    </Box>
  );
};
